import { useEffect, useMemo, useState } from 'react';
import { flattenLeafCategories } from '@/temu-platform/utils/temuCategoryTree';
import {
  isOfficialTemplateScope,
  isPersonalTemplateScope,
  isTeamTemplateScope,
  TEMU_TEMPLATE_SCOPES,
} from '@/temu-platform/constants/temuTemplateScope';
import { isTemuShopAuthorized } from '@/temu-platform/constants/temuRoutes';
import { useTemuShop } from '@/temu-platform/context/TemuShopContext';
import { temuPlatformAPI, fundingAuthAPI, isRequestAborted, getIpeWorkspaceMode } from '@/utils/api';
import { isTeamSpaceEntryAvailable } from '@/pages/ipe/workbench/utils/accountCenterUtils';
import {
  isAllShopsScope,
  isConcreteShopId,
} from '@/utils/platformShopScope';

/**
 * 模板库：单店铺独享按当前店铺 ID；团队店铺共享按当前店类目在工作区可见（不按店铺 ID 隔离）。
 * @param {{ loadCategories?: boolean, preferredShopId?: string }} [options]
 */
export default function useTemuTemplateScope({ loadCategories = true, preferredShopId } = {}) {
  const { activeShop, activeShopId, shops, scopedShops } = useTemuShop();

  const authShopId = useMemo(() => {
    if (isConcreteShopId(preferredShopId)) return String(preferredShopId);
    if (activeShop?.id != null && isConcreteShopId(activeShop.id)) return String(activeShop.id);
    if (isConcreteShopId(activeShopId)) return String(activeShopId);
    const pool = (Array.isArray(scopedShops) && scopedShops.length
      ? scopedShops
      : (shops || []).filter((s) => isTemuShopAuthorized(s)));
    const first = pool.find((s) => s?.id != null);
    return first?.id != null ? String(first.id) : '';
  }, [preferredShopId, activeShop, activeShopId, shops, scopedShops]);

  const listAllShops = preferredShopId != null && String(preferredShopId) !== ''
    ? isAllShopsScope(preferredShopId)
    : isAllShopsScope(activeShopId);

  const [scopeTab, setScopeTab] = useState(TEMU_TEMPLATE_SCOPES.personal);
  const [categories, setCategories] = useState([]);
  const [canAccessTeam, setCanAccessTeam] = useState(false);
  const [workspaceMode, setWorkspaceMode] = useState(() => getIpeWorkspaceMode());

  useEffect(() => {
    const sync = (ev) => {
      const next = ev?.detail?.mode;
      if (next === 'personal' || next === 'team') {
        setWorkspaceMode(next);
        return;
      }
      setWorkspaceMode(getIpeWorkspaceMode());
    };
    window.addEventListener('ipe:workspace-mode-changed', sync);
    window.addEventListener('ipe-workspace-mode-changed', sync);
    window.addEventListener('ipe:workspace-refresh', sync);
    return () => {
      window.removeEventListener('ipe:workspace-mode-changed', sync);
      window.removeEventListener('ipe-workspace-mode-changed', sync);
      window.removeEventListener('ipe:workspace-refresh', sync);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fundingAuthAPI.getAccountCenter()
      .then((center) => {
        if (!cancelled) setCanAccessTeam(isTeamSpaceEntryAvailable(center));
      })
      .catch((err) => {
        if (!cancelled && !isRequestAborted(err)) setCanAccessTeam(false);
      });
    return () => { cancelled = true; };
  }, [workspaceMode]);

  useEffect(() => {
    if (!canAccessTeam && isTeamTemplateScope(scopeTab)) {
      setScopeTab(TEMU_TEMPLATE_SCOPES.personal);
    }
  }, [canAccessTeam, scopeTab]);

  const listShopId = authShopId;
  const categoryShopId = authShopId;
  const canQuery = Boolean(authShopId);
  const isOfficialTab = isOfficialTemplateScope(scopeTab);
  const isPersonalTab = isPersonalTemplateScope(scopeTab);

  const categoryOptions = useMemo(
    () => flattenLeafCategories(categories).map((opt) => ({
      value: opt.categoryId,
      label: opt.label,
      productTypeId: opt.productTypeId,
    })),
    [categories],
  );

  useEffect(() => {
    if (!loadCategories || !categoryShopId) {
      setCategories([]);
      return undefined;
    }
    let cancelled = false;
    temuPlatformAPI.getShopCategories(categoryShopId)
      .then((res) => {
        if (cancelled) return;
        setCategories(res?.categories ?? res?.items ?? res?.list ?? res ?? []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => { cancelled = true; };
  }, [loadCategories, categoryShopId]);

  const listQueryParams = (extra = {}) => ({
    scope: scopeTab,
    ...(listAllShops ? { allShops: true } : {}),
    ...extra,
  });

  const changeScopeTab = (tabId, { onReset } = {}) => {
    if (scopeTab === tabId) return;
    setScopeTab(tabId);
    onReset?.();
  };

  return {
    authShopId,
    scopeTab,
    setScopeTab,
    changeScopeTab,
    listShopId,
    categoryShopId,
    listAllShops,
    canQuery,
    isOfficialTab,
    isPersonalTab,
    canAccessTeam,
    categories,
    setCategories,
    categoryOptions,
    listQueryParams,
    loadCategories: Boolean(loadCategories),
  };
}
